import upperFirst from "lodash/upperFirst";
import { useTranslation } from "react-i18next";

import { useCompanyMachines } from "#modules/machines/view/useCompanyMachines";
import { ProcessStepCatalogDto } from "#modules/quoteRequestLines/infra/fetchProcessStepCatalog.api";
import { SurfaceTreatmentsPredictionsDto } from "#modules/quoteRequestLines/infra/fetchSurfaceTreatmentsPredictions.api";
import { PartialProcessStep } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStep";
import { getProcessStepTitle } from "#shared/view/components/Form/getProcessStepTitle";
import {
  getRelevantMachine,
  IndustrialProcessSchema,
  IndustrialProcessType,
  MaterialGradeType,
} from "shared";

const nonAvailableProcessSteps: IndustrialProcessType[] = [
  IndustrialProcessSchema.enum.SIMPLE,
  IndustrialProcessSchema.enum.MATERIAL,
  IndustrialProcessSchema.enum.PRODUCTS,
];

// separator is used to split the description and the industrial process
// We need to use a separator to compose a unique value for the process step
const SEPARATOR = "@@@@";

// prefix is used to differentiate between company and fabera process steps
// This is useful to avoid duplicates
export const PREFIX = "company-";

type OptionValue = `${typeof PREFIX | ""}${string}${typeof SEPARATOR}${string}`;
type OptionLabel = string;
type Option = {
  label: OptionLabel;
  value: OptionValue;
};

const allFaberaProcessSteps: PartialProcessStep[] = Object.values(IndustrialProcessSchema.enum)
  .filter((step) => !nonAvailableProcessSteps.includes(step))
  .map((industrialProcess, index) => ({
    industrialProcess,
    index,
  }));

export const buildProcessStepOptionValue = (
  processStep: {
    industrialProcess: IndustrialProcessType;
    description?: string | null;
  },
  prefix?: string,
): OptionValue => {
  return `${prefix ?? ""}${processStep.description ?? processStep.industrialProcess}${SEPARATOR}${
    processStep.industrialProcess
  }`;
};

const getProcessStepLabel = (
  processStep: Parameters<typeof getProcessStepTitle>[0]["processStep"],
  materialGrade: MaterialGradeType,
): string => {
  return upperFirst(
    getProcessStepTitle({
      processStep: processStep,
      companyMachine: undefined,
      materialGrade,
    }).toLowerCase(),
  );
};

const isValidIndustrialProcessOptionValue = (
  industrialProcess: string,
): industrialProcess is OptionValue => {
  return industrialProcess.includes(SEPARATOR);
};

const safeParseIndustrialProcessOptionValue = (value: string): OptionValue => {
  if (isValidIndustrialProcessOptionValue(value)) {
    return value;
  }

  return buildProcessStepOptionValue(
    {
      industrialProcess: IndustrialProcessSchema.parse(value),
    },
    PREFIX,
  );
};

export const useProcessStepsSelectData = ({
  allPredictedProcessSteps,
  additionalProcessSteps,
  filterProcessSteps = (processSteps) => processSteps,
  materialGrade,
}: {
  allPredictedProcessSteps: PartialProcessStep[];
  additionalProcessSteps: ProcessStepCatalogDto | SurfaceTreatmentsPredictionsDto | null;
  filterProcessSteps?: <T extends { industrialProcess: IndustrialProcessType }>(
    processSteps: T[],
  ) => T[];
  materialGrade: MaterialGradeType;
}) => {
  const { t } = useTranslation();
  const { companyMachines } = useCompanyMachines();

  const applyDescriptionOverride = (ps: PartialProcessStep): PartialProcessStep => {
    const relevantMachine = getRelevantMachine(
      companyMachines,
      ps.industrialProcess,
      ps.description,
    );
    if (
      relevantMachine?.processStepDescriptionOverride &&
      relevantMachine.processStepDescriptionOverride.length > 0
    ) {
      return { ...ps, description: relevantMachine.processStepDescriptionOverride };
    }
    return ps;
  };

  const companyModelProcessStepsOptions: Option[] = allPredictedProcessSteps.map((ps) => ({
    value: buildProcessStepOptionValue(ps, PREFIX),
    label: getProcessStepLabel(applyDescriptionOverride(ps), materialGrade),
  }));

  const filterCompanyModelProcessSteps = <T extends { industrialProcess: IndustrialProcessType }>(
    processSteps: T[],
  ): T[] =>
    processSteps.filter((step) => {
      return !allPredictedProcessSteps
        .map((ps) =>
          getProcessStepTitle({ processStep: ps, companyMachine: undefined, materialGrade }),
        )
        .includes(
          getProcessStepTitle({
            processStep: { ...step, index: 0 },
            companyMachine: undefined,
            materialGrade,
          }),
        );
    });

  const allAvailableFaberaProcessSteps = filterProcessSteps(
    filterCompanyModelProcessSteps(allFaberaProcessSteps),
  );

  const allFaberaProcessStepsOptions: Option[] = allAvailableFaberaProcessSteps.map((step) => ({
    value: buildProcessStepOptionValue(step),
    label: getProcessStepLabel(step, materialGrade),
  }));

  const companyModelProcessStepsOptionValues = companyModelProcessStepsOptions.map(
    (step) => step.value,
  );

  const restOfFaberaProcessStepsOptions: Option[] = allFaberaProcessStepsOptions.filter(
    (step) => !companyModelProcessStepsOptionValues.includes(step.value),
  );

  const getCompanyProcessStepsItems = () => {
    return companyModelProcessStepsOptions.sort((a, b) => a.label.localeCompare(b.label));
  };

  const getAllProcessStepsOptions = () => {
    return restOfFaberaProcessStepsOptions.sort((a, b) => a.label.localeCompare(b.label));
  };

  const getProcessStepToAddFromSelect = (value: string | null) => {
    if (!value) {
      return null;
    }

    const optionValue = safeParseIndustrialProcessOptionValue(value);

    const processSteptoAdd =
      (additionalProcessSteps &&
        additionalProcessSteps.find((ps) => {
          return buildProcessStepOptionValue(ps, PREFIX) === optionValue;
        })) ||
      allFaberaProcessSteps.find((ps) => buildProcessStepOptionValue(ps) === optionValue);

    return processSteptoAdd ?? null;
  };

  return {
    allProcessStepsDataForSelect: [
      {
        group: t("processStep.myProcessSteps"),
        items: getCompanyProcessStepsItems(),
      },
      {
        group: t("processStep.otherFaberaProcessSteps"),
        items: getAllProcessStepsOptions(),
      },
    ],
    getProcessStepToAddFromSelect,
  };
};
